import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { estiloDeBoton } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { IconoDePortada } from '../piezas';
import { useMisCompetencias } from './api';

/**
 * MisCompetencias — lo que la persona ya tiene, lo que le pide su cargo y con
 * qué cursos cerrar la diferencia.
 *
 * ## «¡No tienes brechas!» cuando no había nada que cubrir
 *
 * Sin competencias definidas para su cargo —o sin cargo en el perfil—, el
 * vanilla ocultaba el avance y enseñaba el trofeo de «Cubres todas las
 * competencias de tu cargo». No era que las cubriera: es que no había ninguna.
 * Ahora lo dice así.
 *
 * ## Quien gestiona también tiene cargo
 *
 * El vanilla solo enseñaba esta vista a quien NO gestiona: un coordinador no
 * podía ver sus propias competencias. Ahora es una pestaña más de su pantalla.
 */
export function MisCompetencias() {
  const mias = useMisCompetencias();

  if (mias.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }
  if (mias.isError || !mias.data) {
    return <EstadoError err={mias.error} que="tus competencias" onReintentar={() => void mias.refetch()} />;
  }

  const d = mias.data;
  const pct = d.expected_total ? Math.round((d.acquired_of_expected / d.expected_total) * 100) : 0;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-3 text-muted">
        {d.position ? (
          <>
            Cargo: <strong className="text-ink-2">{d.position}</strong> ·{' '}
          </>
        ) : null}
        Conocimientos que desarrollas al completar cursos.
      </p>

      {d.expected_total > 0 ? (
        <section aria-labelledby="mc-cargo" className="rounded-md border border-line bg-surface p-4">
          <div className="mb-1.5 flex items-baseline justify-between gap-2 text-3">
            <h2 id="mc-cargo" className="flex items-center gap-1.5 font-semibold text-ink">
              <i className="bi bi-bullseye text-brand-ink" aria-hidden="true" /> Competencias de tu cargo
            </h2>
            <strong className="tabular-nums text-ink-2">
              {d.acquired_of_expected}/{d.expected_total} · {pct} %
            </strong>
          </div>
          <BarraDeProgreso pct={pct} etiqueta="Competencias cubiertas de tu cargo" />
        </section>
      ) : (
        <p className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-3 text-3 text-ink-2">
          <i className="bi bi-info-circle mt-0.5 text-brand-ink" aria-hidden="true" />
          {d.position
            ? `Tu cargo —${d.position}— todavía no tiene competencias definidas.`
            : 'Tu perfil no tiene cargo, así que no hay competencias que se esperen de ti.'}{' '}
          Las que adquieras completando cursos salen igual aquí abajo.
        </p>
      )}

      <section aria-labelledby="mc-adquiridas">
        <h2 id="mc-adquiridas" className="mb-3 flex items-center gap-2 text-4 font-bold text-brand-ink">
          <i className="bi bi-patch-check-fill" aria-hidden="true" /> Adquiridas ({d.acquired.length})
        </h2>
        {d.acquired.length ? (
          <ul className="flex flex-wrap gap-2">
            {d.acquired.map((a) => (
              <li key={a.id}>
                <Badge tone="ok" className="text-2">
                  <i className="bi bi-check-circle-fill" aria-hidden="true" /> {a.name}
                  {a.level_label ? ` · ${a.level_label}` : ''}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <EstadoVacio
            icono="bi-patch-check"
            titulo="Aún no has adquirido competencias"
            descripcion="Se adquieren al completar un curso que las desarrolla."
            compacto
          />
        )}
      </section>

      <section aria-labelledby="mc-brechas">
        <h2 id="mc-brechas" className="mb-3 flex items-center gap-2 text-4 font-bold text-brand-ink">
          <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" /> Brechas por cerrar ({d.gaps.length})
        </h2>
        {d.gaps.length ? (
          <ul className="flex flex-col gap-3">
            {d.gaps.map((g) => (
              <li key={g.id} className="rounded-md border border-line bg-surface p-4">
                <p className="flex flex-wrap items-center gap-2 text-3 font-bold text-ink">
                  <i className="bi bi-mortarboard text-brand-ink" aria-hidden="true" /> {g.name}
                  {g.level_label && <Badge tone="info">{g.level_label}</Badge>}
                </p>
                {g.recommended_courses.length ? (
                  <>
                    <p className="mb-2 mt-2.5 text-2 text-muted">Cursos para cerrarla:</p>
                    <ul className="flex flex-wrap gap-2">
                      {g.recommended_courses.map((rc) => (
                        <li key={rc.course_id}>
                          <a
                            href={`#course-detail/${rc.course_id}`}
                            className={cn(estiloDeBoton({ variant: 'secondary', size: 'sm' }), 'no-underline')}
                          >
                            <span
                              className="flex size-5 items-center justify-center rounded-xs"
                              style={{ background: rc.cover_icon_color || '#0D2B4E' }}
                              aria-hidden="true"
                            >
                              <IconoDePortada valor={rc.cover_icon} respaldo="bi-play-circle" className="text-1 leading-none text-white" />
                            </span>
                            {rc.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="mt-2 text-2 text-muted">
                    Todavía no hay ningún curso publicado que la desarrolle.
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : d.expected_total > 0 ? (
          <p className="flex items-center gap-2 rounded-sm bg-ok/12 px-3 py-2 text-3 font-semibold text-ok-ink">
            <i className="bi bi-trophy-fill" aria-hidden="true" /> Cubres todas las competencias de tu cargo.
          </p>
        ) : (
          <p className="text-3 text-muted">Sin competencias esperadas, no hay brechas que cerrar.</p>
        )}
      </section>
    </div>
  );
}
